import { lazy, type ComponentType } from "react";
import { captureException } from "../config/sentry";

/**
 * Session storage key used to track whether a chunk-load reload
 * has already been attempted for the current page load.
 */
const CHUNK_RELOAD_KEY = "chunk-reload-attempted";

/**
 * Maximum number of reload attempts before giving up and
 * letting the error propagate to the nearest ErrorBoundary.
 */
const MAX_RETRIES = 1;

/** Number of times to retry the dynamic import before falling back to page reload. */
const IMPORT_RETRIES = 2;

/** Delay in milliseconds between import retries. */
const RETRY_DELAY_MS = 1000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Detects whether an error is a chunk/module load failure.
 *
 * Vite and Webpack throw different error types when a dynamic
 * import() fails (usually because the chunk hash changed after
 * a deployment and the user's cached HTML references
 * the old filename).
 *
 * Common patterns:
 * - "Failed to fetch dynamically imported module: …"
 * - "Loading chunk … failed"
 * - "ChunkLoadError"
 * - TypeError with "dynamically imported module" in the message
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  return (
    name === "chunkloaderror" ||
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("loading chunk") ||
    message.includes("loading css chunk") ||
    (message.includes("failed to fetch") && message.includes("module"))
  );
}

/**
 * A drop-in replacement for React.lazy() that automatically
 * retries chunk loads by triggering a page reload when a stale
 * chunk 404s after a deployment.
 *
 * Safety mechanism:
 * - Uses sessionStorage to track reload attempts
 * - Limits to MAX_RETRIES reloads to prevent infinite loops
 * - After exhausting retries, the error propagates to the nearest
 *   ErrorBoundary for graceful degradation
 *
 * @example
 * ```ts
 * // Before
 * const MyPage = lazy(() => import("./pages/MyPage"));
 *
 * // After
 * const MyPage = lazyWithRetry(() => import("./pages/MyPage"));
 * ```
 */
export function lazyWithRetry<T extends ComponentType<Record<string, unknown>>>(
  importFn: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const component = await importFn();
      clearChunkReloadFlag();
      return component;
    } catch (error) {
      if (isChunkLoadError(error)) {
        // Phase 1: Retry the import itself (handles transient failures)
        for (let i = 0; i < IMPORT_RETRIES; i++) {
          await delay(RETRY_DELAY_MS);
          try {
            const component = await importFn();
            clearChunkReloadFlag();
            return component;
          } catch {
            // continue to next retry
          }
        }

        // Import retries exhausted — report to Sentry before attempting page reload
        captureException(error, {
          tags: { chunk_error_phase: "retries_exhausted" },
          level: "warning",
        });

        // Phase 2: Full page reload (gets fresh index.html with new chunk URLs)
        const attempts = getReloadAttempts();
        if (attempts < MAX_RETRIES) {
          setReloadAttempts(attempts + 1);
          window.location.reload();
          return new Promise<{ default: T }>(() => {});
        }

        // Phase 3: Exhausted — clear flag and propagate to ErrorBoundary
        clearChunkReloadFlag();
      }

      throw error;
    }
  });
}

/** Get the current reload attempt count from sessionStorage. */
function getReloadAttempts(): number {
  try {
    const value = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    return value ? parseInt(value, 10) : 0;
  } catch {
    // sessionStorage may be unavailable (e.g. private browsing in some browsers)
    return 0;
  }
}

/** Set the reload attempt count in sessionStorage. */
function setReloadAttempts(count: number): void {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, count.toString());
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
}

/** Clear the chunk reload flag from sessionStorage. */
export function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
}
